import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a fixed discount code in place, without a
 * page navigation. Wraps the existing `.cart-discount-apply` anchor, which
 * remains a working `/discount/{code}` link if this script never runs or the
 * request fails.
 *
 * Uses the same mechanism as `cart-discount.js`: POST the full discount list
 * to `Theme.routes.cart_update_url` asking for the cart section back, then
 * morph that section in place. The applied-state markup and the updated
 * totals are rendered by Liquid, so there is no client-side UI to build.
 *
 * @typedef {Object} CartDiscountApplyRefs
 * @property {HTMLAnchorElement} applyLink - The discount anchor.
 *
 * @extends {Component<CartDiscountApplyRefs>}
 */
class CartDiscountApply extends Component {
  requiredRefs = ['applyLink'];

  /** @type {boolean} */
  #pending = false;

  /** @type {string | null} */
  #restingLabel = null;

  /**
   * Applies the discount without navigating.
   * @param {MouseEvent} event - The click event on the anchor.
   */
  apply = async (event) => {
    const link = this.refs.applyLink;
    const sectionId = this.dataset.sectionId;
    const code = this.dataset.discountCode;

    // Missing wiring: do nothing and let the anchor navigate as it does today.
    if (!link || !sectionId || !code) return;

    event.preventDefault();

    // Guard against a second tap while the first is still in flight.
    if (this.#pending) return;

    // Already applied: no-op rather than re-POSTing the same code.
    const existingDiscounts = this.#existingDiscounts(sectionId);
    if (existingDiscounts.includes(code)) return;

    this.#setPending(true);

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          // Append, never replace — preserves any discount already on the cart.
          discount: [...existingDiscounts, code].join(','),
          sections: [sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, config);
      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);

      const data = await response.json();
      const newHtml = data?.sections?.[sectionId];
      if (!newHtml) throw new Error('Cart update returned no section markup');

      // Hydration mode inside a drawer keeps the drawer open; full mode on the
      // cart page. Same switch `cart-discount.js` uses.
      morphSection(sectionId, newHtml, {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });

      // On success this element is replaced by the applied-state markup, so
      // there is deliberately no pending state to unwind here.
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    } catch (error) {
      // Never leave a dead button: fall back to the anchor's own behaviour,
      // which still applies the discount and returns to the cart.
      this.#setPending(false);
      window.location.href = link.href;
    }
  };

  /**
   * Toggles the pending state on the anchor. Anchors do not support the
   * `disabled` property, so this uses `aria-disabled` plus a pointer-events
   * guard — the accessible equivalent of the `button.disabled` pattern in
   * `product-form.js`.
   *
   * @param {boolean} isPending - Whether a request is in flight.
   */
  #setPending(isPending) {
    const link = this.refs.applyLink;
    if (!link) return;

    this.#pending = isPending;

    if (isPending) {
      this.#restingLabel = link.textContent;
      link.setAttribute('aria-disabled', 'true');
      link.style.pointerEvents = 'none';

      const pendingLabel = this.dataset.pendingLabel;
      if (pendingLabel) link.textContent = pendingLabel;
      return;
    }

    link.removeAttribute('aria-disabled');
    link.style.pointerEvents = '';
    if (this.#restingLabel !== null) link.textContent = this.#restingLabel;
  }

  /**
   * Reads the discount codes already on the cart, scoped to this cart section
   * so the drawer and the page instance cannot read each other's pills.
   *
   * @param {string} sectionId - The section this component belongs to.
   * @returns {string[]}
   */
  #existingDiscounts(sectionId) {
    const scope = document.getElementById(`shopify-section-${sectionId}`) ?? document;

    /** @type {string[]} */
    const discountCodes = [];
    for (const pill of scope.querySelectorAll('.cart-discount__pill')) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-apply-component')) {
  customElements.define('cart-discount-apply-component', CartDiscountApply);
}
